import { Bounce, toast } from 'react-toastify';
import {
    ISameCaseMatchCaseResponseModel,
    useGetSearchSameCasesAdmin,
} from './useGetSearchSameCasesAdmin';
import {
    ISameCaseMatchedCasesSearchModel,
    SameCaseSortColumns,
} from '../../../../pages/quad/same-cases-admin/manage/useGetSameCasesQuery';
import { ISameCaseModel, ISaveSameCaseModel } from '../../../../interfaces/ISameCaseModel';
import { showErrorToast, showSuccessToast } from '../../../../shared/ToastUtils';
import { useEffect, useState } from 'react';

import { CheckmatePagination } from '../../../../pages/quad/CheckmatePagination';
import { Loader } from '../../../shared/Loader';
import { LoadingButton } from '../../../../shared/buttons/LoadingButton';
import { MainContentOverlayComponent } from '../../../shared/MainContentOverlay';
import { PageWithAffixedHeader } from '../../../../shared/PageWithAffixedHeader';
import { SameCaseDetailsSelectorModal } from '../SameCaseDetailsSelectorModal';
import SameCasesTable from '../../same-cases/view/SameCasesTable';
import { SortDirections } from '../../../../utilities/Constants';
import { Stack } from 'react-bootstrap';
import { cloneDeep } from 'lodash';
import { useCaseFileModelContext } from '../../../../contexts/useCaseFileModelContext';
import { useDidUpdate } from '@mantine/hooks';
import { useSaveSameCaseMutation } from '../../../../pages/quad/same-cases-admin/manage/useSaveSameCaseMutation';

export interface ISameCaseSearchProps {
    selectedCases: ISameCaseMatchCaseResponseModel[];
    caseNumberSearchValue: string | undefined;
    jurisdictionSearchValue: string | undefined;
    caseFileModeSearchValue: number;
    onHide: () => void;
    onSaveCaseGroupComplete: (caseGroup: ISaveSameCaseModel | undefined) => void;
}

export function SameCaseSearch(props: ISameCaseSearchProps) {
    const saveCaseGroupMutation = useSaveSameCaseMutation();
    const { caseFileModelId } = useCaseFileModelContext();

    const [selectedCaseGroup, setSelectedCaseGroup] = useState<ISameCaseModel>();
    const [
        showCaseGroupDetailsSelectorModalForExisting,
        setShowCaseGroupDetailsSelectorModalForExisting,
    ] = useState<boolean>(false);
    const [
        showCaseGroupDetailsSelectorModalForCreate,
        setShowCaseGroupDetailsSelectorModalForCreate,
    ] = useState<boolean>(false);

    const [currentFilters, setCurrentFilters] = useState<ISameCaseMatchedCasesSearchModel>({
        caseNumber: props.caseNumberSearchValue,
        jurisdictions: props.jurisdictionSearchValue ? [props.jurisdictionSearchValue] : undefined,
        caseFileModelId: props.caseFileModeSearchValue,
        pagedModel: {
            pageNumber: 1,
            pageSize: 10,
        },
        sortModel: {
            sortColumn: SameCaseSortColumns.CaseNumber,
            sortDirection: SortDirections.Asc,
        },
    } as ISameCaseMatchedCasesSearchModel);

    const caseGroupSearchApi = useGetSearchSameCasesAdmin(currentFilters);

    const saveCaseGroup = (caseGroup: ISameCaseModel, newCaseGuids: string[]) => {
        const newCaseGroup = {
            ...caseGroup,
            caseGuids: caseGroup.caseGuids.concat(newCaseGuids),
        } as ISameCaseModel;

        saveCaseGroupMutation
            .mutateAsync({
                guid: newCaseGroup.guid,
                caseFileModelId: caseFileModelId,
                caseGuids: newCaseGroup.caseGuids,
                caseName: newCaseGroup.caseName,
                caseNumber: newCaseGroup.caseNumber,
                includeRejectedCases: currentFilters.showAllRejectedSameCases,
                jurisdiction: newCaseGroup.jurisdiction,
            } as ISaveSameCaseModel)
            .then((updatedCaseGroup) => {
                props.onSaveCaseGroupComplete(updatedCaseGroup);

                showSuccessToast(
                    <span>
                        Same Case for <span className="bold">{caseGroup.caseNumber}</span> updated!
                    </span>
                );
            })
            .catch(() =>
                showErrorToast(
                    `Failed to save the Same Case for - ${caseGroup.caseNumber} | ${caseGroup.jurisdiction} | ${caseGroup.caseName}`
                )
            );
    };

    const handleSelectedCaseChange = (caseGroup: ISameCaseModel) => {
        setSelectedCaseGroup(caseGroup);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentFilters((prev) => ({ ...prev, pagedModel: { ...prev.pagedModel, pageNumber } }));
        setSelectedCaseGroup(undefined);
    };

    const handlePageSizeChange = (pageSize: number) => {
        setCurrentFilters((prev) => ({
            ...prev,
            pagedModel: { ...prev.pagedModel, pageSize, pageNumber: 1 },
        }));
        setSelectedCaseGroup(undefined);
    };

    const handleSaveClick = () => {
        if (!selectedCaseGroup) {
            return;
        }

        let caseName = '';
        let caseNumber = '';

        const hasDifferentCaseDetails = !props.selectedCases.every(
            (mc) =>
                mc.caseName === selectedCaseGroup.caseName &&
                mc.caseNumber === selectedCaseGroup.caseNumber
        );

        if (hasDifferentCaseDetails) {
            setShowCaseGroupDetailsSelectorModalForExisting(true);
        } else {
            if (props.selectedCases.length > 0) {
                caseName = props.selectedCases?.[0].caseName;
                caseNumber = props.selectedCases?.[0].caseNumber;
            }

            const selectedCaseGroupCopy = cloneDeep(selectedCaseGroup);
            selectedCaseGroupCopy.caseName = caseName;
            selectedCaseGroupCopy.caseNumber = caseNumber;

            saveCaseGroup(
                selectedCaseGroupCopy,
                props.selectedCases.map((selectedCase) => selectedCase.caseGuid)
            );
        }
    };

    const handleCreateNewSameCaseClick = () => {
        if (props.selectedCases.length < 2) {
            alert(
                'No selected cases detected. Please select at least one case to create a Same Case.'
            );
            return;
        }

        let caseName = '';
        let caseNumber = '';

        const firstSelectedCase = props.selectedCases.at(0);

        const hasDifferentCaseDetails = !props.selectedCases.every(
            (mc) =>
                mc.caseName === firstSelectedCase?.caseName &&
                mc.caseNumber === firstSelectedCase?.caseNumber
        );

        if (hasDifferentCaseDetails) {
            setShowCaseGroupDetailsSelectorModalForCreate(true);
        } else {
            caseName = firstSelectedCase?.caseName ?? '';
            caseNumber = firstSelectedCase?.caseNumber ?? '';

            const newCaseGroup: ISameCaseModel = {
                caseGuids: [],
                caseName,
                caseNumber,
                guid: '',
                jurisdiction: firstSelectedCase?.jurisdiction ?? '',
            };

            saveCaseGroup(
                newCaseGroup,
                props.selectedCases.map((selectedCase) => selectedCase.caseGuid)
            );
        }
    };

    const handleCancelClick = () => {
        props.onHide();
        setSelectedCaseGroup(undefined);
    };

    const handleCaseGroupDetailsSelectedForExisting = (
        caseGroupName: string,
        caseGroupNumber: string
    ) => {
        if (!selectedCaseGroup) {
            return;
        }

        const selectedCaseGroupCopy = cloneDeep(selectedCaseGroup);
        selectedCaseGroupCopy.caseName = caseGroupName;
        selectedCaseGroupCopy.caseNumber = caseGroupNumber;

        setShowCaseGroupDetailsSelectorModalForExisting(false);

        saveCaseGroup(
            selectedCaseGroupCopy,
            props.selectedCases.map((selectedCase) => selectedCase.caseGuid)
        );
    };

    const handleCaseGroupDetailsSelectedForCreate = (
        caseGroupName: string,
        caseGroupNumber: string
    ) => {
        const newCaseGroup: ISameCaseModel = {
            guid: '',
            caseName: caseGroupName,
            caseNumber: caseGroupNumber,
            caseGuids: [],
            jurisdiction: props.selectedCases[0]?.jurisdiction ?? '',
        };

        setShowCaseGroupDetailsSelectorModalForExisting(false);

        saveCaseGroup(
            newCaseGroup,
            props.selectedCases.map((selectedCase) => selectedCase.caseGuid)
        );
    };

    const handleSortData = (key: string, order: string) => {
        const sortColumn = parseInt(key) as SameCaseSortColumns;
        const sortDirection =
            order.toUpperCase() === 'DESC' ? SortDirections.Desc : SortDirections.Asc;

        if (
            sortColumn === currentFilters.sortModel.sortColumn &&
            sortDirection === currentFilters.sortModel.sortDirection
        ) {
            return;
        }

        setCurrentFilters((prev) => ({
            ...prev,
            pagedModel: { ...prev.pagedModel, pageNumber: 1 },
            sortModel: {
                sortColumn,
                sortDirection,
            },
        }));
        setSelectedCaseGroup(undefined);
    };

    useDidUpdate(() => {
        caseGroupSearchApi.refetch();
    }, [
        currentFilters.pagedModel.pageSize,
        currentFilters.pagedModel.pageNumber,
        currentFilters.sortModel.sortColumn,
        currentFilters.sortModel.sortDirection,
    ]);

    useEffect(() => {
        setSelectedCaseGroup(undefined);
    }, []);

    const hasRows = (caseGroupSearchApi?.data?.results?.length ?? 0) > 0;
    const selectedCaseCount = props.selectedCases.length;

    const caseDisplayNames = props.selectedCases.map((currentCase) => {
        return `${currentCase?.caseNumber} | ${currentCase?.jurisdiction} | ${currentCase?.caseName} | ${currentCase?.organizationName}`;
    });

    const distinctCaseNames = Array.from(
        new Set(props.selectedCases.map((caseItem) => caseItem.caseName))
    );

    const distinctCaseNumbers = Array.from(
        new Set(props.selectedCases.map((caseItem) => caseItem.caseNumber))
    );

    const distinctCaseNamesWithSelected = Array.from(
        new Set(distinctCaseNames.concat(selectedCaseGroup?.caseName ?? '').filter((x) => !!x))
    );

    const distinctCaseNumbersWithSelected = Array.from(
        new Set(distinctCaseNumbers.concat(selectedCaseGroup?.caseNumber ?? '').filter((x) => !!x))
    );

    const pageHeader = (
        <>
            <Stack
                className="mt-3"
                direction="horizontal"
                style={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
                <h5>
                    Choose a Same Case for the selected case:
                    {selectedCaseCount > 1 && <>s</>}
                </h5>
                <Stack direction="horizontal" style={{ justifyContent: 'flex-start' }}>
                    {hasRows && (
                        <LoadingButton
                            text="Select Same Case"
                            loadingText="Saving..."
                            loading={saveCaseGroupMutation.isLoading}
                            buttonCssClass="btn-orange"
                            onClick={handleSaveClick}
                            disabled={!selectedCaseGroup}
                        />
                    )}

                    <button className="btn btn-default" onClick={handleCancelClick}>
                        Back
                    </button>
                </Stack>
            </Stack>
            <div className="mt-3 p-3" style={{ background: 'whitesmoke' }}>
                {selectedCaseCount === 1 ? (
                    <span className="bold">Selected Case</span>
                ) : (
                    <span className="bold">{selectedCaseCount} Selected Cases</span>
                )}

                <hr style={{ marginTop: '0.5em' }} />
                <div className="p-3 pt-1 pb-1">
                    <ul>
                        {caseDisplayNames.map((caseName, i) => (
                            <li key={caseName + i}>{caseName}</li>
                        ))}
                    </ul>
                </div>
            </div>
            <Stack direction="horizontal" className="mt-4" gap={3} style={{ alignItems: 'center' }}>
                <h5>Available Same Cases</h5>
            </Stack>
            <hr style={{ marginTop: '0.5em', marginBottom: '0.5em' }} />
        </>
    );

    const pageBody = (
        <>
            {caseGroupSearchApi?.data?.results?.length === 0 ? (
                <NoResultsDisplay
                    onCreateSameCaseClick={handleCreateNewSameCaseClick}
                    isSaving={saveCaseGroupMutation.isLoading}
                    selectedCaseCount={props.selectedCases.length}
                />
            ) : (
                <div className="px-3">
                    <SameCasesTable
                        currentFilters={currentFilters}
                        onSelectedSameCase={handleSelectedCaseChange}
                        onSort={handleSortData}
                        results={caseGroupSearchApi?.data?.results ?? []}
                        selectedCaseGroup={selectedCaseGroup}
                    />
                    <CheckmatePagination
                        pagedModel={caseGroupSearchApi?.data?.pagedModel}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        hidePageSizeSelector
                    />
                </div>
            )}

            {showCaseGroupDetailsSelectorModalForExisting && (
                <SameCaseDetailsSelectorModal
                    currentCaseGroupName={
                        selectedCaseGroup?.caseName ?? props.selectedCases[0]?.caseName
                    }
                    currentCaseGroupNumber={
                        selectedCaseGroup?.caseNumber ?? props.selectedCases[0]?.caseNumber
                    }
                    distinctNames={distinctCaseNamesWithSelected}
                    distinctNumbers={distinctCaseNumbersWithSelected}
                    onCancelClick={() => setShowCaseGroupDetailsSelectorModalForExisting(false)}
                    onSubmitClick={handleCaseGroupDetailsSelectedForExisting}
                />
            )}

            {showCaseGroupDetailsSelectorModalForCreate && (
                <SameCaseDetailsSelectorModal
                    currentCaseGroupName={props.selectedCases[0]?.caseName}
                    currentCaseGroupNumber={props.selectedCases[0]?.caseNumber}
                    distinctNames={distinctCaseNames}
                    distinctNumbers={distinctCaseNumbers}
                    onCancelClick={() => setShowCaseGroupDetailsSelectorModalForCreate(false)}
                    onSubmitClick={handleCaseGroupDetailsSelectedForCreate}
                />
            )}
        </>
    );

    return (
        <MainContentOverlayComponent>
            <PageWithAffixedHeader headerComponent={pageHeader} hideHeaderBottomPadding>
                {caseGroupSearchApi.isFetching && <Loader />}
                {pageBody}
            </PageWithAffixedHeader>
        </MainContentOverlayComponent>
    );
}

function NoResultsDisplay(props: {
    selectedCaseCount: number;
    onCreateSameCaseClick: () => void;
    isSaving: boolean;
}) {
    return (
        <Stack gap={3} style={{ textAlign: 'center', marginTop: '2em' }}>
            <div>
                There are no available Same Cases for the selected{' '}
                {props.selectedCaseCount === 1 ? 'case' : 'cases'}.
            </div>
            {props.selectedCaseCount > 1 && (
                <Stack gap={3}>
                    If you'd like to create a new Same Case for the selected cases, click the button
                    below.
                    <div>
                        <LoadingButton
                            text="Create New Same Case"
                            loadingText="Saving..."
                            loading={props.isSaving}
                            buttonCssClass="btn btn-orange"
                            onClick={props.onCreateSameCaseClick}
                        ></LoadingButton>
                    </div>
                </Stack>
            )}
        </Stack>
    );
}
